import { MessageType } from './model.ts';
import {
  getBeatCountForSypNote,
  getBeatCountForTimeSignature,
  getMaxBeatCountForSypChord,
  getMinBeatCountForSypChord,
  getTimeSignature,
  isSypChord,
  Notes,
  StaffIndex,
  SypChord,
  SypFileMessage,
  SypMeasureNew,
  SypNote,
} from './syp_file.ts';
import { isClose } from './util.ts';

export const fermataBeats = 2;

export function getMeasureBeatCount(measure: SypMeasureNew, { includeFermatas }: { includeFermatas?: boolean } = {}) {
  const beatCountsPerStaff = measure.staves.map(({ notes }) => getBeatCountForNotes({ notes, includeFermatas }));
  return Math.max(...beatCountsPerStaff);
}

interface NoteVisit {
  note: SypNote | SypChord;
  startsAtBeat: number;
}

export function visitStaffNotes({
  measures,
  staffIndex,
  messages,
  includeFermatas,
}: {
  measures: SypMeasureNew[];
  staffIndex: StaffIndex;
  includeFermatas?: boolean;
  messages?: SypFileMessage[];
}): NoteVisit[] {
  const visits: NoteVisit[] = [];
  for (const measure of measures) {
    const measureNotes = measure.staves[staffIndex].notes;
    visits.push(
      ...visitMeasureNotes({ notes: measureNotes, includeFermatas, messages, measureNumber: measure.number }),
    );
  }
  return visits;
}

export function visitMeasureNotes({
  notes,
  messages,
  measureNumber,
  includeFermatas,
}: {
  notes: Notes;
  includeFermatas?: boolean;
  messages?: SypFileMessage[];
  measureNumber?: number;
}): NoteVisit[] {
  // If a single clef contains heterogeneous chords (one chord with notes having different durations),
  // then we treat the measure as having multiple voices.  We don't care which voice is which - we
  // just need to visit the notes in the order they should be played in real time.
  // For a good example, see church song 324-021 measure 3, piano voice
  const visits: NoteVisit[] = [];
  let firstBeatCount = 0;
  let secondBeatCount = 0;
  for (const maybeChord of notes) {
    let minBeatCount: number;
    let maxBeatCount: number;
    visits.push({ note: maybeChord, startsAtBeat: Math.min(firstBeatCount, secondBeatCount) });
    if (isSypChord(maybeChord)) {
      minBeatCount = getMinBeatCountForSypChord(maybeChord, { includeFermatas });
      maxBeatCount = getMaxBeatCountForSypChord(maybeChord, { includeFermatas });
    } else {
      minBeatCount = maxBeatCount = getBeatCountForSypNote(maybeChord, { includeFermatas });
    }
    if (isClose(firstBeatCount, secondBeatCount)) {
      firstBeatCount += minBeatCount;
      secondBeatCount += maxBeatCount;
    } else {
      if (minBeatCount < maxBeatCount) {
        messages?.push({
          type: MessageType.Error,
          text: `Consecutive heterogeneous chords in measure ${measureNumber}`,
        });
      }

      if (firstBeatCount < secondBeatCount) {
        firstBeatCount += minBeatCount;
      } else {
        secondBeatCount += minBeatCount;
      }
    }
  }
  return visits;
}

export function getBeatCountForNotes({
  notes,
  includeFermatas,
  messages,
  measureNumber,
}: {
  notes: Notes;
  includeFermatas?: boolean;
  messages?: SypFileMessage[];
  measureNumber?: number;
}): number {
  const visits = visitMeasureNotes({ notes, includeFermatas, messages, measureNumber });
  if (visits.length === 0) {
    return 0;
  }
  const lastVisit = visits[visits.length - 1];
  const { startsAtBeat, note } = lastVisit;
  return (
    startsAtBeat +
    (isSypChord(note)
      ? getMaxBeatCountForSypChord(note, { includeFermatas })
      : getBeatCountForSypNote(note, { includeFermatas }))
  );
}

export function isFullBeatCount(measure: SypMeasureNew): boolean {
  const timeSignature = getTimeSignature(measure);
  if (!timeSignature) {
    throw new Error(`All measures must have a rhythm`);
  }
  const expectedMeasureBeats = getBeatCountForTimeSignature(timeSignature);
  const measureBeatCount = getMeasureBeatCount(measure);
  return measureBeatCount >= expectedMeasureBeats;
}

export function getFirstMeasureNumber(measure: SypMeasureNew): number {
  return isFullBeatCount(measure) ? 1 : 0;
}
